/**
 * Decal Tool
 * 
 * Places image decals on surfaces.
 */

import { v4 as uuidv4 } from 'uuid';
import { DecalElement, DecalGeometryData } from '../core/types';
import { BaseTool } from './BaseTool';
import { ThreeEvent } from '@react-three/fiber';
import * as THREE from 'three';

export class DecalTool extends BaseTool {
    id = 'decal';
    name = 'Decal';
    cursor = 'crosshair';

    private currentPoint: THREE.Vector3 | null = null;
    private currentNormal: THREE.Vector3 = new THREE.Vector3(0, 0, 1);
    private currentHostId: string | null = null;

    // Default image path (would come from dialog in real app)
    private imagePath: string = '/images/logo.png';
    private decalWidth: number = 1.0;
    private decalHeight: number = 0.5;

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        if (event.button !== 0) return;

        if (this.currentPoint && this.currentHostId) {
            this.createDecal();
        }
    }

    onPointerMove(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;

        // Find host surface
        const result = this.hostManager?.findHost(event.intersections, {
            categories: ['Walls', 'Floors', 'Ceilings', 'Generic Models']
        });

        if (result) {
            this.currentPoint = result.point;
            this.currentNormal = result.normal;
            this.currentHostId = result.elementId;
        } else {
            this.currentPoint = null;
            this.currentHostId = null;
        }

        this.updatePreview();
    }

    onPointerUp(_event: ThreeEvent<PointerEvent>): void { }

    onKeyDown(event: KeyboardEvent): void {
        if (event.key === 'Escape') {
            this.deactivate();
        }
    }

    private updatePreview() {
        if (!this.context) return;

        if (!this.currentPoint) {
            this.context.appStore.setToolPreview(null);
            return;
        }

        this.context.appStore.setToolPreview({
            type: 'plane',
            position: this.currentPoint,
            size: [this.decalWidth, this.decalHeight],
            normal: this.currentNormal,
            color: '#ffffff',
            opacity: 0.7
        });
    }

    private createDecal() {
        if (!this.context || !this.currentPoint || !this.currentHostId) return;

        const geometryData: DecalGeometryData = {
            hostId: this.currentHostId,
            position: [this.currentPoint.x, this.currentPoint.y, this.currentPoint.z],
            normal: [this.currentNormal.x, this.currentNormal.y, this.currentNormal.z],
            width: this.decalWidth,
            height: this.decalHeight,
            imagePath: this.imagePath
        };

        const decal: DecalElement = {
            id: uuidv4(),
            category: 'Decals',
            type: 'Decal',
            parameters: {
                'Image': this.imagePath,
                'Width': `${this.decalWidth} m`,
                'Height': `${this.decalHeight} m`
            },
            constraints: [
                { type: 'HOST_FACE', targetId: this.currentHostId, data: {} }
            ],
            geometryData
        };

        this.context.bimStore.addElement(decal);
        console.log(`Created Decal: ${decal.id}`);
    }
}
